import { forwardRef, useCallback, useEffect, useState, useRef } from "react"
import debounce from "lodash.debounce"

import useCombinedRefs from "web/hooks/behaviour/useCombinedRefs"
import { isObjectWithProperty } from "web/services/helpers"
import { formValuesAreDifferent } from "web/components/Form/helpers"

import { formatValue } from "web/services/format"

import classNames from "classnames"

import ControlContainer from "../ControlContainer"
import UnitSelector from "./UnitSelector"

const InputWithUnit = forwardRef((props, ref) => {
  const {
    name,
    fieldKey,
    value,
    format = null,
    kind,
    units,
    touched,
    error,
    defaultUnit: defaultUnitPropValue,
    onChange,
    onFocus = () => {},
    onBlur = () => {},
    placeholder,
    autoFocus,
    disabled = false,
  } = props

  const defaultUnit = defaultUnitPropValue || units[0]

  const innerRef = useRef()
  const inputRef = useCombinedRefs(ref, innerRef)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _onChange = useCallback(
    debounce((value) => {
      onChange(value.value === null ? null : value)
    }, 200),
    [onChange, value]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _onBlur = useCallback(debounce(onBlur, 200), [onBlur])

  useEffect(() => {
    if (autoFocus) {
      inputRef.current.focus()
    }

    return () => {
      _onChange.flush()
      _onBlur.flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getValidValue = useCallback(
    (value) => {
      if (value === null || value === undefined || value === "") {
        return { value: null, unit: defaultUnit }
      } else {
        const validValue = !isObjectWithProperty(value, "value")
          ? null
          : value.value

        const validUnit =
          !isObjectWithProperty(value, "unit") || value.unit === null
            ? defaultUnit
            : value.unit

        return {
          value: validValue,
          unit: validUnit,
          kind,
          format,
        }
      }
    },
    [defaultUnit, kind, format]
  )

  const [hasFocus, setHasFocus] = useState(false)

  const [localValue, setLocalValue] = useState(getValidValue(value))

  useEffect(() => {
    const newLocalValue = getValidValue(value)

    if (formValuesAreDifferent(localValue, newLocalValue)) {
      setLocalValue(newLocalValue)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultUnit])

  function handleFocus(e) {
    _onBlur.cancel()

    setHasFocus(true)
    onFocus(e)
  }

  function handleBlur() {
    _onChange.flush()

    setHasFocus(false)
    _onBlur()
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" || e.key === "Escape") {
      e.stopPropagation()
      e.target.blur()
    }
  }

  function changeAmount(amount) {
    const newValueBlob = {
      ...localValue,
      value: amount === "" ? null : amount,
      kind,
      format,
    }

    _onChange(newValueBlob)
    setLocalValue(newValueBlob)
  }

  function handleAmountChange(e) {
    const newValue = e.target.value
    changeAmount(newValue)
  }

  function parseInput(value) {
    return value.replace(/[^0-9.]/g, "")
  }

  function handlePaste(event) {
    event.preventDefault()

    const clipboardText = (event.clipboardData || window.clipboardData).getData(
      "text"
    )

    const inputElement = event.target
    const { value, selectionStart, selectionEnd } = inputElement

    const newAmount =
      value.slice(0, selectionStart) +
      parseInput(clipboardText) +
      value.slice(selectionEnd)

    changeAmount(newAmount)
  }

  function handleUnitChange(e) {
    const newUnit = e.target.value
    const newValueBlob = { ...localValue, unit: newUnit, kind, format }

    _onChange.flush()
    setLocalValue(newValueBlob)
    onChange(newValueBlob)
    inputRef.current.focus()
  }

  const inputAmount = localValue.value === null ? "" : localValue.value

  let errorToDisplay
  if (touched && error) {
    errorToDisplay = error
  } else if (inputAmount !== "" && isNaN(inputAmount)) {
    errorToDisplay = "Please enter a number."
  }

  return (
    <ControlContainer
      {...props}
      error={errorToDisplay}
      errorCondition={!!errorToDisplay}
    >
      <div
        className="grid items-center"
        style={{ gridTemplateColumns: "1fr 80px" }}
      >
        <input
          type="text"
          key={`money-input-${fieldKey}`}
          ref={inputRef}
          data-testid={`form-input-${name}`}
          className={classNames("form-control", {
            "mock-focus": hasFocus,
            "opacity-50": disabled,
          })}
          value={
            hasFocus
              ? inputAmount
              : formatValue(localValue, { showUnit: false }) || inputAmount
          }
          onChange={handleAmountChange}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        <UnitSelector
          value={localValue.unit}
          onChange={handleUnitChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          units={units}
        />
      </div>
    </ControlContainer>
  )
})

export default InputWithUnit
